import {MeetingCommentFile} from './MeetingCommentFile'
import {ErrorResponseBody} from '../../../network/response/ErrorResponseBody'
import EventEmitterEx from '../../shared/pageBaseComponents/EventEmitterEx'
import {Nullable} from '../../../app/TypeHelpers'
import {FileDataForValidate} from './FileDataForValidate'

export interface FileUploadError {
    commentId: number
    errorFileNameList: string[]
}

export abstract class SubmitCommentAndFilesTemplate<S> {
    commentId: Nullable<number>
    registerCommentError: (inputError: {
        title: string,
        messages: string[],
        fileNameList?: string[],
    }) => void
    eventEmitter: EventEmitterEx
    errorFileNameList: string[]

    protected constructor(
        registerCommentError: (inputError: {
            title: string,
            messages: string[],
            fileNameList?: string[],
        }) => void,
        eventEmitter: EventEmitterEx,
    ) {
        this.commentId = null
        this.registerCommentError = registerCommentError
        this.eventEmitter = eventEmitter
        this.errorFileNameList = []
    }

    abstract registerComment(sendData: S): Promise<Nullable<number>>

    abstract registerFile(sendData: S, commentId: number, commentFile: MeetingCommentFile): Promise<void>

    abstract validateFile(fileDataForValidate: FileDataForValidate): Promise<Nullable<void>>

    getFileUploadError(): Nullable<FileUploadError> {
        if (this.commentId === null || this.errorFileNameList.length === 0) {
            return null
        }

        return {
            commentId: this.commentId,
            errorFileNameList: this.errorFileNameList,
        }
    }

    getErrorMessages(errorResponseBody: ErrorResponseBody): string[] {
        const errorMessages: string[] = []
        errorResponseBody.requestError.message.split('\n')
            .forEach(message => errorMessages.push(message))
        errorResponseBody.requestItemErrors
            .forEach(itemError => errorMessages.push(itemError.message))
        return errorMessages
    }

    getErrorFileNames(
        errorResponseBody: ErrorResponseBody,
        fileDataForValidate: FileDataForValidate,
    ): string[] {
        const errorFileNames: string[] = []
        errorResponseBody.requestItemErrors
            .forEach(itemError => {
                if (itemError.fields === undefined) {
                    return
                }
                const workIndex = itemError.fields[0].split('[')[1]
                const fileIndex = Number(workIndex.split(']')[0])
                errorFileNames.push(fileDataForValidate.fileNameList[fileIndex])
            })
        return errorFileNames
    }

    protected async process(sendData: S, commentFileList: MeetingCommentFile[]): Promise<boolean> {
        await this.eventEmitter.showLoading()
        this.errorFileNameList = []
        try {
            const registerCommentFileList: MeetingCommentFile[] = []
            commentFileList.forEach(commentFile => {
                const fileData: string = commentFile.fileNumber === null ?
                    String(commentFile.fileData).split(',')[1] : ''
                if (fileData) {
                    registerCommentFileList.push({
                        fileNumber: commentFile.fileNumber,
                        fileName: commentFile.fileName,
                        fileData: fileData,
                        fileSize: commentFile.fileSize,
                    })
                }
            })
            const fileCount = registerCommentFileList.length
            if (fileCount !== 0) {
                const fileDataForValidate: FileDataForValidate = {
                    fileNameList: registerCommentFileList.map(file => file.fileName),
                }
                const result = await this.validateFile(fileDataForValidate)
                if (result === null) {
                    return false
                }
            }

            this.commentId = await this.registerComment(sendData)
            if (this.commentId === null) {
                return false
            }

            for (let index = 0; index < fileCount; index++) {
                this.eventEmitter.changeLoadingMessage(`ファイルアップロード中... ${index + 1}/${fileCount}`)

                const commentFile = registerCommentFileList[index]
                try {
                    await this.registerFile(sendData, this.commentId, commentFile)
                } catch (e) {
                    registerCommentFileList.slice(index)
                        .forEach(file => this.errorFileNameList.push(file.fileName))
                    break
                }
            }
        } finally {
            this.eventEmitter.closeLoading()
        }

        return true
    }
}
